import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";
import { useTranslation } from "react-i18next";

const GEO_URL =
  "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// All presence markers rendered as circular flag badges on top of the map.
// USA is anchored to New York rather than filling the entire country.
// "anchor" controls how each flag is cropped into its circle so key elements stay visible.
const MARKERS = [
  { code: "us", coords: [-74.0, 40.7], anchor: "xMidYMid slice" },
  { code: "ae", coords: [54.5, 24.3], anchor: "xMinYMid slice" },
  { code: "ug", coords: [34.2, 1.8], anchor: "xMidYMid slice" },
  { code: "cd", coords: [21.5, -3.5], anchor: "xMidYMid slice" },
  { code: "za", coords: [25.5, -29.0], anchor: "xMidYMid slice" },
];

const FLAG_URL = (code) => `https://flagcdn.com/w1280/${code}.png`;

function FlagBadge({ code, name, anchor = "xMidYMid slice", r = 14 }) {
  const clipId = `clip-${code}`;
  return (
    <g data-testid={`impact-marker-${code}`}>
      {/* Pulsing halo */}
      <circle
        r={r + 6}
        fill="#FFFFFF"
        opacity={0.14}
        className="pin-pulse"
        style={{ transformOrigin: "center" }}
      />
      {/* White outer ring for contrast */}
      <circle r={r + 2.5} fill="#FFFFFF" opacity={0.95} />
      <clipPath id={clipId}>
        <circle r={r} />
      </clipPath>
      <image
        href={FLAG_URL(code)}
        xlinkHref={FLAG_URL(code)}
        x={-r * 1.4}
        y={-r}
        width={r * 2.8}
        height={r * 2}
        preserveAspectRatio={anchor}
        clipPath={`url(#${clipId})`}
      />
      {/* Inner border stroke */}
      <circle r={r} fill="none" stroke="#0B1A3D" strokeWidth={1.1} />
      <title>{name}</title>
    </g>
  );
}

export default function WorldMap() {
  const { t } = useTranslation();
  const markerNames = t("data.markers", { returnObjects: true });
  return (
    <div className="w-full" data-testid="impact-world-map">
      <div className="relative w-full rounded-2xl overflow-hidden border border-slate-200 shadow-[0_20px_60px_rgba(11,26,61,0.18)] bg-[#0B1A3D]">
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at 50% 45%, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0) 55%)",
          }}
        />
        <ComposableMap
          projectionConfig={{ scale: 165 }}
          style={{ width: "100%", height: "auto", position: "relative" }}
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  style={{
                    default: {
                      fill: "#1B2D5A",
                      stroke: "rgba(255,255,255,0.22)",
                      strokeWidth: 0.4,
                      outline: "none",
                    },
                    hover: {
                      fill: "#243C76",
                      stroke: "rgba(255,255,255,0.30)",
                      strokeWidth: 0.4,
                      outline: "none",
                    },
                    pressed: { outline: "none" },
                  }}
                />
              ))
            }
          </Geographies>

          {MARKERS.map((m) => (
            <Marker key={m.code} coordinates={m.coords}>
              <FlagBadge
                code={m.code}
                name={markerNames[m.code]}
                anchor={m.anchor}
              />
            </Marker>
          ))}
        </ComposableMap>

        <div className="absolute top-5 left-6 pointer-events-none">
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/60">
            {t("impact.mapOverline")}
          </p>
        </div>
      </div>

      <div
        data-testid="impact-map-legend"
        className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3"
      >
        {MARKERS.map((p) => (
          <div
            key={p.code}
            data-testid={`impact-country-${p.code}`}
            className="flex items-center gap-3 rounded-full border border-slate-200 bg-white px-4 py-2.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
          >
            <img
              src={`https://flagcdn.com/w40/${p.code}.png`}
              alt={`${markerNames[p.code]} flag`}
              className="h-4 w-6 object-cover rounded-sm shrink-0 ring-1 ring-slate-200"
            />
            <span className="text-sm font-semibold text-[#0B1A3D] truncate">
              {markerNames[p.code]}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
